<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import MessageMeta from '../MessageMeta.vue';
import { useMessageContext } from '../provider.js';
import {
  MEDIA_CARD_WIDTH,
  PHOTO_MOBILE_MAX_WIDTH,
} from './mediaAspectLayout.js';

const props = defineProps({
  icon: { type: [String, Object], required: true },
  iconBgColor: { type: String, default: 'bg-n-alpha-3' },
  senderTranslationKey: { type: String, required: true },
  // Lado do circulo da esquerda, em px. Vira prop porque o cartao de contato
  // cresceu para a largura da foto e um circulo de 40px ficava perdido nele;
  // a localizacao e a chamada continuam no tamanho antigo pelo padrao.
  iconSize: { type: Number, default: 40 },
  // Tipografia do par titulo/conteudo. Vira prop porque o cartao de CONTATO
  // segue as proporcoes do WhatsApp (nome grande e forte, telefone logo
  // abaixo), enquanto a localizacao e a chamada continuam com o corpo miudo
  // do cartao generico.
  titleClass: { type: String, default: 'text-sm font-medium' },
  contentClass: { type: String, default: 'text-[13px]' },
  content: { type: String, required: true },
  caption: { type: String, default: '' },
  title: { type: String, default: '' }, // Title can be any name, description, etc
  action: {
    type: Object,
    required: true,
    validator: action => {
      return action.label && (action.href || action.onClick);
    },
  },
});

const { sender } = useMessageContext();
const { t } = useI18n();

const senderName = computed(() => {
  return sender?.value?.name || '';
});

// Aceleres 2026-08-18: o cartão deixou de ser visivelmente mais estreito
// (19rem = 304px) que a mídia da conversa e passou a acompanhá-la.
//
// 2026-09-02: a referência é agora o vídeo/GIF (MEDIA_CARD_WIDTH, 420px), e
// não mais a foto — ela ganhou caixa própria de 336x344 nesse dia e deixou de
// ser a mídia mais larga da conversa. Continua a vir de uma constante
// partilhada, e não de um número escrito aqui, para não divergir no primeiro
// ajuste feito só de um lado.
const cardWidthStyle = computed(() => ({
  width: `min(${MEDIA_CARD_WIDTH}px, ${PHOTO_MOBILE_MAX_WIDTH})`,
}));

const iconBoxStyle = computed(() => ({
  width: `${props.iconSize}px`,
  height: `${props.iconSize}px`,
}));
</script>

<template>
  <!-- O horário é desenhado aqui dentro (`hide-meta`), e não pelo Base.vue.
       Lá ele é absoluto no canto inferior do balão, o que no cartão o jogava
       PARA BAIXO do botão de ação — no WhatsApp ele fica acima do divisor,
       fechando a linha do contato. -->
  <BaseBubble
    hide-meta
    class="overflow-hidden px-3 py-2.5"
    :style="cardWidthStyle"
    data-bubble-name="attachment"
  >
    <div class="flex min-w-0 flex-col gap-3">
      <div class="flex min-w-0 items-center gap-3">
        <div
          class="grid shrink-0 place-content-center overflow-hidden rounded-full"
          :class="iconBgColor"
          :style="iconBoxStyle"
        >
          <slot name="icon">
            <Icon :icon="icon" class="size-5 text-white" />
          </slot>
        </div>
        <div class="min-w-0 flex-1 space-y-0.5 overflow-hidden">
          <slot>
            <div
              v-if="title"
              class="truncate text-n-slate-12"
              :class="titleClass"
            >
              {{ title }}
            </div>
            <div
              v-if="content"
              class="truncate text-n-slate-11"
              :class="contentClass"
            >
              {{ content }}
            </div>
            <div
              v-if="caption"
              class="text-sm text-n-slate-12 whitespace-pre-wrap"
            >
              {{ caption }}
            </div>
          </slot>
        </div>
      </div>
      <MessageMeta class="-mt-1.5 justify-end text-n-slate-11" />
      <!-- A acao e so o texto: sem fundo, sem realce ao passar o mouse. O
           divisor acima continua a separa-la do corpo do cartao. -->
      <div v-if="action" class="-mx-3 -mb-2.5 border-t border-n-weak">
        <a
          v-if="action.href"
          :href="action.href"
          rel="noreferrer noopener nofollow"
          target="_blank"
          class="block h-11 w-full px-4 text-center text-sm font-medium leading-[2.75rem] text-[#00a884]"
        >
          {{ action.label }}
        </a>
        <button
          v-else
          class="h-11 w-full px-4 text-center text-sm font-medium text-[#00a884]"
          @click="action.onClick"
        >
          {{ action.label }}
        </button>
      </div>
    </div>
  </BaseBubble>
</template>
